import type {
  AuthScopeSource,
  TenantRolesSource,
} from "@playbookai/api-clients";
import type { AuthScope } from "@playbookai/types";
import { canAdministerWorkspace } from "./roles.js";
import type { AuthState } from "./auth-store.js";

const listeners = new Set<() => void>();
let snapshot: AuthScope | null = null;

export const authScopeSource: AuthScopeSource = Object.freeze({
  getSnapshot: () => snapshot,
  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
});

const canAdministerWorkspaceListeners = new Set<() => void>();
let canAdministerWorkspaceSnapshot = false;

/**
 * Reactive workspace-administration seam: whether the current user is a
 * tenant admin. Module-independent — LAMA is the first consumer (gating
 * questionnaire/campaign creation), not the only intended one. Sibling
 * publisher to `authScopeSource`, same subscribe shape, so
 * `useSyncExternalStore` consumers re-render the moment the underlying claims
 * change (e.g. a mid-session role change), rather than reading a value on
 * some unrelated re-render.
 */
export const canAdministerWorkspaceSource = Object.freeze({
  getSnapshot: () => canAdministerWorkspaceSnapshot,
  subscribe: (listener: () => void) => {
    canAdministerWorkspaceListeners.add(listener);
    return () => canAdministerWorkspaceListeners.delete(listener);
  },
});

const tenantRolesListeners = new Set<() => void>();
const NO_ROLES: readonly string[] = Object.freeze([]);
let tenantRolesSnapshot: readonly string[] = NO_ROLES;

/**
 * Reactive tenant-ROLE seam (ADR-0078): the session's realm roles, frozen, for
 * a module deriving an additive-role affordance (LAMA's `canManageLama`). A
 * third sibling publisher rather than a `canManageLama` boolean, so the next
 * additive role does not cost another boolean seam on `ApiClientConfig`.
 * Published only when the SET changes, so a re-render of the claims object
 * with the same roles does not wake every subscriber.
 */
export const tenantRolesSource: TenantRolesSource = Object.freeze({
  getSnapshot: () => tenantRolesSnapshot,
  subscribe: (listener: () => void) => {
    tenantRolesListeners.add(listener);
    return () => tenantRolesListeners.delete(listener);
  },
});

export function publishTenantRoles(state: AuthState): void {
  const claims = state.status === "authenticated" ? state.claims : null;
  const next = claims ? Object.freeze([...claims.roles]) : NO_ROLES;

  if (sameRoles(tenantRolesSnapshot, next)) return;
  tenantRolesSnapshot = next;
  for (const listener of [...tenantRolesListeners]) listener();
}

export function publishAuthScope(state: AuthState): void {
  const claims = state.status === "authenticated" ? state.claims : null;
  const next =
    claims?.tenantId &&
    claims.userId.trim().length > 0 &&
    claims.authSessionGeneration &&
    claims.authSessionGeneration.trim().length > 0
      ? Object.freeze({
          tenantId: claims.tenantId,
          actorUserId: claims.userId,
          authSessionGeneration: claims.authSessionGeneration,
        })
      : null;

  if (sameScope(snapshot, next)) return;
  snapshot = next;
  for (const listener of [...listeners]) listener();
}

export function publishCanAdministerWorkspace(state: AuthState): void {
  const claims = state.status === "authenticated" ? state.claims : null;
  const next = canAdministerWorkspace(claims);

  if (canAdministerWorkspaceSnapshot === next) return;
  canAdministerWorkspaceSnapshot = next;
  for (const listener of [...canAdministerWorkspaceListeners]) listener();
}

export function __resetAuthScopeForTests(): void {
  snapshot = null;
  listeners.clear();
  canAdministerWorkspaceSnapshot = false;
  canAdministerWorkspaceListeners.clear();
  tenantRolesSnapshot = NO_ROLES;
  tenantRolesListeners.clear();
}

function sameRoles(left: readonly string[], right: readonly string[]): boolean {
  return (
    left === right ||
    (left.length === right.length &&
      left.every((role, index) => role === right[index]))
  );
}

function sameScope(left: AuthScope | null, right: AuthScope | null): boolean {
  return (
    left === right ||
    (left !== null &&
      right !== null &&
      left.tenantId === right.tenantId &&
      left.actorUserId === right.actorUserId &&
      left.authSessionGeneration === right.authSessionGeneration)
  );
}
